// src/contexts/sector/SectorReturnsProvider.tsx
/**
 * SectorReturnsProvider - Centralizes sector returns data fetching
 * 
 * Provides:
 * - Raw returns data for charts and custom calculations
 * - Pre-computed returns by sector ID
 * - Benchmark market cap data for RRG/momentum charts
 */

import React, { createContext, useMemo } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { useTopSectors } from "@/hooks/dashboard/useTopSectors";
import { 
  categoryHistoryCompactQueryOptions, 
  totalMarketCapHistoryQueryOptions 
} from "@/services/queries/dashboards/categoriesQueries";
import { tokenHistoryQueryOptions } from "@/services/queries/dashboards/tokenHistoryQueries";
import { MetricKeys } from "@/constants/metrics";
import { toReturns } from "@/lib/metrics";
import type { CategoryHistoryCompactResponse } from "@/types/dashboards/categories";
import type { LeafCategorySnapshot } from "@/hooks/dashboard/useLeafCategorySnapshots";

// ============================================================================
// TYPES
// ============================================================================

export interface Sector {
  id: number;
  name: string;
  code: string;
  mcap: number;
}

/** Benchmark market cap data for RRG and momentum charts */
export interface BenchmarkMarketCap {
  btc: CategoryHistoryCompactResponse | undefined;
  eth: { metrics?: Array<{ dates: number[]; values: (number | null)[] }> } | undefined;
  total: { dates: number[]; values: (number | null)[] } | undefined;
  isLoading: boolean;
}

export interface SectorReturnsContextValue {
  /** Top sectors being tracked */
  sectors: Sector[];
  
  /** Leaf category snapshots (includes backend-computed metrics) */
  leaves: LeafCategorySnapshot[];
  
  /** Raw API responses indexed by sector ID - for charts */
  rawDataById: Map<number, CategoryHistoryCompactResponse>;
  
  /** BTC category info */
  btcSector: { id: number; name: string } | null;
  
  /** Pre-computed returns by sector ID (for calculations) */
  returnsBySectorId: Record<number, number[]>;
  
  /** Benchmark market cap data for RRG/momentum charts */
  benchmarkMarketCap: BenchmarkMarketCap;
  
  /** Loading state */
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  
  /** Refetch all data */
  refetch: () => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const SectorReturnsContext = createContext<SectorReturnsContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface SectorReturnsProviderProps {
  /** Number of top sectors to include */
  topN?: number;
  children: React.ReactNode;
}

export const SectorReturnsProvider: React.FC<SectorReturnsProviderProps> = ({
  topN = 30,
  children,
}) => {
  // Get top sectors AND leaf snapshots (includes backend-computed metrics)
  const { topSectors, leaves, btcCategory, isLoading: sectorsLoading } = useTopSectors({
    count: topN,
    includeBtc: true,
  });
  
  // Build list of all sector IDs to fetch
  const sectorIds = useMemo(() => {
    const ids = topSectors.map(s => s.id);
    if (btcCategory?.id && !ids.includes(btcCategory.id)) {
      ids.push(btcCategory.id);
    }
    return ids;
  }, [topSectors, btcCategory]);
  
  // Batch fetch returns data for all sectors
  const queries = useQueries({
    queries: sectorIds.map(id => ({
      ...categoryHistoryCompactQueryOptions(id, [MetricKeys.ReturnsDailyTS]),
      enabled: id > 0,
      staleTime: 10 * 60 * 1000,
    })),
  });
  
  // Fetch benchmark market cap data (for RRG and momentum charts)
  const btcMarketCapQuery = useQuery({
    ...categoryHistoryCompactQueryOptions(btcCategory?.id ?? 0, [MetricKeys.MarketCap]),
    enabled: Boolean(btcCategory?.id),
    staleTime: 10 * 60 * 1000,
  });
  
  const ethMarketCapQuery = useQuery({
    ...tokenHistoryQueryOptions("ETH", [MetricKeys.MarketCap]),
    staleTime: 10 * 60 * 1000,
  });
  
  const totalMarketCapQuery = useQuery(totalMarketCapHistoryQueryOptions());
  
  const value = useMemo<SectorReturnsContextValue>(() => {
    // Build raw data map (for charts)
    const rawDataById = new Map<number, CategoryHistoryCompactResponse>();
    sectorIds.forEach((id, idx) => {
      const data = queries[idx]?.data;
      if (data) {
        rawDataById.set(id, data);
      }
    });
    
    // Pre-compute returns for convenience
    const returnsBySectorId: Record<number, number[]> = {};
    rawDataById.forEach((data, id) => {
      const metric = data.metrics?.find(m => m.metric_key === MetricKeys.ReturnsDailyTS);
      if (metric?.values) {
        const prices = metric.values.map(v => v ?? NaN);
        returnsBySectorId[id] = toReturns(prices);
      }
    });
    
    const isLoading = sectorsLoading || queries.some(q => q.isLoading);
    const isError = queries.some(q => q.isError);
    const error = queries.find(q => q.error)?.error as Error | null;
    
    // Benchmark market cap for RRG/momentum
    // Transform ETH data to match expected shape (dates required in metric objects)
    const ethData = ethMarketCapQuery.data;
    const ethTransformed = ethData?.metrics ? {
      metrics: ethData.metrics
        .filter(m => m.dates && m.values)
        .map(m => ({ dates: m.dates!, values: m.values! }))
    } : undefined;
    
    const benchmarkMarketCap: BenchmarkMarketCap = {
      btc: btcMarketCapQuery.data,
      eth: ethTransformed,
      total: totalMarketCapQuery.data,
      isLoading: btcMarketCapQuery.isLoading || ethMarketCapQuery.isLoading || totalMarketCapQuery.isLoading,
    };
    
    return {
      sectors: topSectors,
      leaves,
      rawDataById,
      btcSector: btcCategory ? { id: btcCategory.id, name: btcCategory.name } : null,
      returnsBySectorId,
      benchmarkMarketCap,
      isLoading,
      isError,
      error,
      refetch: () => {
        queries.forEach(q => q.refetch());
        btcMarketCapQuery.refetch();
        ethMarketCapQuery.refetch();
        totalMarketCapQuery.refetch();
      },
    };
  }, [topSectors, leaves, sectorIds, queries, btcCategory, sectorsLoading, btcMarketCapQuery, ethMarketCapQuery, totalMarketCapQuery]);
  
  return (
    <SectorReturnsContext.Provider value={value}>
      {children}
    </SectorReturnsContext.Provider>
  );
};

export { SectorReturnsContext };
